import { useState, useRef, useEffect, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, ApiError } from '../lib/api';
import { fieldErrorsFromDetails } from '../lib/fieldErrorsFromDetails';
import { HelpPopover } from './help';
import GitHubRepoSetupSection, { type GitHubSetupPayload, isLinkExistingIncomplete } from './GitHubRepoSetupSection';
import DeliveryModePicker, {
  DEFAULT_CHECKPOINTS,
  defaultCheckpointKeys,
  checkpointKey,
} from './DeliveryModePicker';
import {
  CURRENT_TRADEOFF_COPY_VERSION,
  PROTOTYPE_AGENT_GOVERNING_PRINCIPLE,
  PROTOTYPE_AGENT_FIDELITY_COPY,
} from '@blueprint/shared';

// ADR-003 (2026-07-31) — HIGH is the Prototype Agent's only fidelity now;
// this array always has exactly one entry (prototypeAgentTradeoffCopy.ts).
// There is no selector — opt-in is on/off, HIGH implied.
const PROTOTYPE_TRADEOFF_ENTRY = PROTOTYPE_AGENT_FIDELITY_COPY[0];

interface NewProjectModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const PHASES = [
  { value: 'phase_1_requirements', label: 'Phase 1 — Requirements' },
  { value: 'phase_2_architecture', label: 'Phase 2 — Architecture' },
  { value: 'phase_2_5_ux_design', label: 'Phase 2.5 — UX Design' },
  { value: 'phase_3_sprint_planning', label: 'Phase 3 — Sprint Planning' },
  { value: 'phase_4_development', label: 'Phase 4 — Development' },
  { value: 'phase_5_code_review', label: 'Phase 5 — Code Review' },
  { value: 'phase_6_testing', label: 'Phase 6 — Testing' },
  { value: 'phase_7_deployment', label: 'Phase 7 — Deployment' },
] as const;

// Sprint 12 DOMAIN.A2 — pack selector. Default Generic (no
// regulatory additions); the agents pick this up at next session
// start via projects.domain_pack_id (PATCH /projects/:id/domain-pack
// runs after the project insert). Mirrors the canonical types in
// apps/api/src/db/schema/domain-packs.ts.
const DOMAIN_PACKS = [
  { value: 'generic',    label: 'Generic — no industry-specific context' },
  { value: 'healthcare', label: 'Healthcare — HIPAA / NABH / WCAG 2.1 AA' },
  { value: 'bfsi',       label: 'BFSI — RBI / SEBI cyber-resilience' },
] as const;
type DomainPackValue = (typeof DOMAIN_PACKS)[number]['value'];

// #2013 — a STUDENT on more than one cohort team gets a 400 from POST
// /projects (routes/projects.ts's "ambiguous, do not guess" branch)
// unless the request includes cohortTeamId. This mirrors that shape on
// the frontend: fetch the creator's own teams, and only ask when there's
// a real choice to make.
interface CohortTeamOption {
  id:         string;
  name:       string;
  cohortId:   string;
  cohortName: string;
  semester:   string;
}

// MODE.B1 (#1174) — Delivery Mode picker (with its DEFAULT_CHECKPOINTS
// profile, key helpers, and Track-B card UI) extracted to
// ./DeliveryModePicker so BrownfieldOnboardingPage can reuse the same
// surface. The state shape (deliveryMode + selectedCheckpoints Set)
// stays in the modal so the submit-time payload assembly and disable
// guard can read it directly.

export default function NewProjectModal({ onClose, onSuccess }: NewProjectModalProps) {
  const navigate = useNavigate();
  // MODE.Cfix.A1 — the DeliveryModePicker is shown to every tenant. The
  // old MODE.A2 Trial-hide (and its useMyTenant billing lookup) is gone:
  // governance is now a universal, billing-independent choice, and the
  // submit handler always sends an explicit deliveryMode so the picked
  // value (Governed included) reaches the API.
  const [name, setName] = useState('');
  const [productArea, setProductArea] = useState('');
  const [startingPhase, setStartingPhase] = useState('phase_1_requirements');
  const [domainPack, setDomainPack] = useState<DomainPackValue>('generic');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // #2013 — the creator's own cohort teams, populated REACTIVELY: only
  // after a create attempt 400s with the "more than one cohort team"
  // shape (routes/projects.ts:741-746, `{ error, cohortTeamIds }`), never
  // on mount. Deliberately not an on-open fetch (see handleSubmit's catch
  // block for the full reasoning) — this keeps every 0-or-1-team user's
  // create flow (i.e. everyone today) byte-for-byte unchanged: no extra
  // request, no new render-blocking state, nothing to await before the
  // form is interactive.
  const [cohortTeamOptions, setCohortTeamOptions] = useState<CohortTeamOption[]>([]);
  const [selectedCohortTeamId, setSelectedCohortTeamId] = useState('');
  // Only meaningful once cohortTeamOptions is populated (i.e. after the
  // reactive fetch above has run) — mirrors minCheckpointGuard/
  // prototypeAgentGuard immediately below: the submit button reads this
  // to disable, and handleSubmit rechecks it before setLoading(true) for
  // the same state-transition-race reason those two guards document.
  const cohortTeamPickerGuard = cohortTeamOptions.length > 0 && !selectedCohortTeamId;

  // Sprint 12.5 ACC.B3 (#417) — Delivery Mode selection. Default is
  // Governed (Track A behaviour, identical to every project shipped
  // before B1). Selecting Accelerate reveals the checkpoint toggle
  // group, default-populated with Phase 1/5/7 EXIT (the canonical B2
  // mandatory-review profile). At least one checkpoint must remain
  // selected; the Create button disables otherwise.
  const [deliveryMode, setDeliveryMode] = useState<'governed' | 'accelerate'>('governed');
  const [selectedCheckpoints, setSelectedCheckpoints] = useState<Set<string>>(
    () => defaultCheckpointKeys(),
  );
  // #1115B S8 — GitHub setup payload collected from <GitHubRepoSetupSection />.
  // Null until the user picks a mode; included in the POST /projects body
  // as `github: { mode, ... }` when non-null. Omission preserves seeder
  // backwards-compat (no `github` field on the body).
  const [githubPayload, setGithubPayload] = useState<GitHubSetupPayload | null>(null);
  // #1153 D5 — Controlled inline-error state for the GitHubRepoSetupSection.
  // Distinct from the form-level `error` slot (line :277-281): a bind-step
  // failure renders inside the section so the radio group + [Connect GitHub →]
  // affordance are co-located with the GitHub controls (per recon §1.2 + D5).
  // Cleared by onRetry (operator-explicit) or onModeChange (V4 — user toggles
  // a different mode in the radio group, signalling intent to recover).
  const [githubErrorMsg, setGithubErrorMsg] = useState<string | null>(null);
  const [githubErrorNextAction, setGithubErrorNextAction] = useState<
    'reconnect_github' | 'check_github_status' | null
  >(null);
  const clearGithubError = () => {
    setGithubErrorMsg(null);
    setGithubErrorNextAction(null);
  };

  // #3037 AC2 — server validation messages for the Link-existing inputs
  // (details[] paths github.owner / github.repo). A field's message clears
  // when that field's value changes; a mode change alone keeps both, and
  // neither touches the bind-step error above.
  const [githubFieldErrors, setGithubFieldErrors] = useState<{ owner?: string; repo?: string }>({});
  const handleGithubPayloadChange = (next: GitHubSetupPayload | null) => {
    const prev = githubPayload;
    setGithubPayload(next);
    if (prev?.mode !== 'link_existing' || next?.mode !== 'link_existing') return;
    const ownerChanged = next.owner !== prev.owner;
    const repoChanged = next.repo !== prev.repo;
    if (ownerChanged || repoChanged) {
      setGithubFieldErrors(fe => ({
        ...(ownerChanged ? {} : { owner: fe.owner }),
        ...(repoChanged ? {} : { repo: fe.repo }),
      }));
    }
  };

  // #3037 AC1 / Q4 (ii) — when an error is set, scroll the dialog's own
  // scroll container: to the top, where the banner is, whenever the banner
  // has text (it sits above every field); otherwise to the first field
  // showing an error. Runs when the banner text changes or the catch below
  // sets field errors (fieldErrorSeq), not when an edit clears one.
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [fieldErrorSeq, setFieldErrorSeq] = useState(0);
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const field = error ? null : container.querySelector<HTMLElement>('[aria-invalid="true"]');
    if (!error && !field) return;
    container.scrollTop = field
      ? field.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop
      : 0;
  }, [error, fieldErrorSeq]);
  const minCheckpointGuard =
    deliveryMode === 'accelerate' && selectedCheckpoints.size === 0;

  // PROTO.A1 (#1619) — Prototype Agent opt-in, selected at project setup
  // (D2 — creation-time, not a post-creation settings toggle, unlike
  // claudeDesignExportEnabled). Default off (AC1).
  // ADR-003 (2026-07-31) — opt-in is now on/off only; HIGH is implied.
  // The prior per-fidelity `prototypeFidelity` state (and the "any
  // fidelity-value change clears viewed+acknowledged" handler it needed,
  // since a Low→High change was a materially different tradeoff) is gone —
  // there is no fidelity value left to change between.
  const [prototypeAgentEnabled, setPrototypeAgentEnabled] = useState(false);
  // D8 — viewed is set ONLY by the explicit "Read the tradeoffs" click
  // below, never by default (mirrors termsViewed at
  // PartnerOnboardingWizardPage.tsx:830-832). The acknowledged checkbox
  // stays disabled until viewed === true.
  const [tradeoffCopyViewed, setTradeoffCopyViewed] = useState(false);
  const [tradeoffCopyAcknowledged, setTradeoffCopyAcknowledged] = useState(false);

  const handlePrototypeAgentToggle = (checked: boolean) => {
    setPrototypeAgentEnabled(checked);
    if (!checked) {
      setTradeoffCopyViewed(false);
      setTradeoffCopyAcknowledged(false);
    }
  };

  // AC2 — save is gated until the tradeoff copy has been acknowledged
  // (which itself requires viewed === true first). Fidelity is implied
  // HIGH once enabled — nothing left to select.
  const prototypeAgentGuard = prototypeAgentEnabled && !tradeoffCopyAcknowledged;

  // #3037 AC3 — Link-existing needs a trimmed, non-empty Owner and Repository
  // before the POST goes out.
  const githubLinkGuard = isLinkExistingIncomplete(githubPayload);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    // ACC.B3 (#417) — frontend min-1-checkpoint guard.
    //
    // The submit button is `disabled` when `minCheckpointGuard` is
    // true, so the only way this branch is reachable is a state-
    // transition race. We early-return BEFORE setting `loading=true`
    // (Round 1 review BLOCKER): an earlier draft set
    // `setLoading(true)` first and returned without a paired
    // `setLoading(false)`, leaving the spinner stuck.
    if (minCheckpointGuard) {
      setError('Accelerate Mode requires at least one Review Checkpoint.');
      return;
    }

    // PROTO.A1 (#1619) R1 finding M2 — mirrors minCheckpointGuard's recheck
    // immediately above, same reason: the submit button is `disabled` when
    // `prototypeAgentGuard` is true, so this branch is reachable only via a
    // state-transition race. Rechecking here (not just trusting the
    // `disabled` attribute) matters more for this guard than most: the
    // payload below constructs `acknowledgedAt`/`tradeoffCopyVersion` as an
    // audit record of genuine consent (D8) — building that record from a
    // stale `disabled` state would persist a consent timestamp for an
    // acknowledgment that didn't actually happen. Early-return BEFORE
    // `setLoading(true)`, same as minCheckpointGuard, so the spinner never
    // gets stuck on this path either.
    if (prototypeAgentGuard) {
      setError('Acknowledge the tradeoffs before creating the project.');
      return;
    }

    // #2013 — mirrors the two guards above: the submit button is disabled
    // when `cohortTeamPickerGuard` is true, so this is a state-transition-
    // race backstop, not the primary defense. Early-return BEFORE
    // `setLoading(true)`, same reason as both guards above.
    if (cohortTeamPickerGuard) {
      setError('Select which team this project is for.');
      return;
    }

    // #3037 AC3 — same state-transition-race backstop as the guards above:
    // the submit button is disabled when `githubLinkGuard` is true.
    if (githubLinkGuard) {
      setError('Enter both Owner and Repository to link an existing repo.');
      return;
    }

    setLoading(true);
    setError('');
    // #1153 — clear any prior bind-step error before re-submit.
    clearGithubError();
    setGithubFieldErrors({});

    // ACC.B3 (#417) — translate the wizard's Set<key> back into the
    // shape the backend schema expects. Send an explicit checkpoints
    // array only when the user has tweaked the default (selected !==
    // all three defaults). Sending the full default profile vs
    // omitting the field is functionally identical (B1's seeding hook
    // produces the same rows for the omitted case), but explicit is
    // clearer when reading the request payload during debugging.
    const checkpointsPayload = deliveryMode === 'accelerate'
      ? DEFAULT_CHECKPOINTS
          .filter((c) => selectedCheckpoints.has(checkpointKey(c)))
          .map((c) => ({ phase: c.phase, gateType: c.gateType }))
      : undefined;

    try {
      const created = await apiFetch<{ id: string }>('/projects', {
        method: 'POST',
        body: JSON.stringify({
          name: name.trim(),
          ...(productArea.trim() ? { productArea: productArea.trim() } : {}),
          currentPhase: startingPhase,
          // #2013 — sent only on a resubmit after the picker appeared
          // (reactively, from a prior 400 — see handleSubmit's catch
          // block) and a team was chosen. Omitted on every first attempt
          // and every 0-or-1-team creator, so the server's existing
          // auto-link (1 team) / no-op (0 teams) behavior is unchanged.
          ...(selectedCohortTeamId ? { cohortTeamId: selectedCohortTeamId } : {}),
          // MODE.B6 (#1179): tenantId + createdBy omitted — the server
          // resolves both from the auth context (membership + userId) and
          // discards any body-supplied values (routes/projects.ts:494,527-529).
          // MODE.Cfix.A1 — always send the explicit deliveryMode (both
          // 'governed' and 'accelerate'). The picker is shown to every
          // tenant now, so a Governed pick MUST reach the API: the backend
          // no longer infers a Trial→Accelerate default, and a conditional
          // spread that omitted 'governed' would let the column default
          // (or any future default) override the user's explicit choice.
          deliveryMode,
          ...(checkpointsPayload && { checkpoints: checkpointsPayload }),
          // #1115B S8 — include `github` only when the user picked a mode.
          // Omission triggers the backwards-compat path (BootstrapAgent's
          // existing tenantInfra-defaulted CREATE_REPO flow). Per V3
          // verification: spread guard with truthy check prevents
          // `github: undefined` from leaking into the JSON body where Zod
          // would reject it.
          ...(githubPayload ? { github: githubPayload } : {}),
          // PROTO.A1 (#1619) — omitted entirely when the toggle is off,
          // mirroring the domainPack-omission-when-default convention a
          // few lines below; the backend schema's refine treats a missing
          // `prototypeAgent` field identically to `{ enabled: false }`.
          // ADR-003 — fidelity is always 'HIGH' now; there is no selection
          // to read, so this no longer needs to gate on a fidelity value
          // being present, only on the toggle itself.
          ...(prototypeAgentEnabled
            ? {
                prototypeAgent: {
                  enabled: true,
                  fidelity: 'HIGH' as const,
                  acknowledgedAt: new Date().toISOString(),
                  tradeoffCopyVersion: CURRENT_TRADEOFF_COPY_VERSION,
                },
              }
            : {}),
        }),
      });

      // Sprint 12 DOMAIN.A2 — apply domain pack after project create.
      // Skipped when the user kept the Generic default since that's
      // the runtime fallback anyway and avoids an unnecessary write
      // to projects.domain_pack_id + an audit-log row.
      let packFailed = false;
      if (domainPack !== 'generic') {
        try {
          await apiFetch(`/projects/${created.id}/domain-pack`, {
            method: 'PATCH',
            body: JSON.stringify({ packType: domainPack }),
          });
        } catch (packErr) {
          // Non-fatal: the project was created, so still take the success
          // path below. #3505 — the navigation carries the failure to the
          // project's Overview, where DomainPackFailedNotice tells the user.
          console.warn('[NewProjectModal] domain pack assignment failed:', packErr);
          packFailed = true;
        }
      }

      // #2002 — navigate straight to the created project instead of just
      // closing over a silent list refetch. A STUDENT's newly-created
      // project can legitimately take a moment to appear in GET /projects
      // (or, before this fix, never appeared at all) — closing the modal
      // with no other feedback is what caused the 2026-08-04 incident:
      // students saw nothing happen and clicked Create again, producing
      // duplicate projects that can never be deleted (audit_logs FK — see
      // #2005). Navigating directly to the project is the strongest
      // available confirmation and removes the reason to click again.
      onSuccess();
      onClose();
      navigate(
        packFailed
          ? `/projects/${created.id}?${new URLSearchParams({ notice: 'domain_pack_failed', pack: domainPack })}`
          : `/projects/${created.id}`,
      );
    } catch (err) {
      // #1153 D5 — branch on ApiError + nextAction to route bind-step failures
      // into the inline section error (with [Connect GitHub →] / [Retry] CTAs),
      // and everything else (auth errors, validation errors, etc.) into the
      // form-level slot. The nextAction field is the discriminator per D8's
      // uniform wire shape — the modal does NOT need to know which endpoint
      // (POST /projects or POST /:id/github-repo) produced the 422.
      if (
        err instanceof ApiError &&
        (err.body.nextAction === 'reconnect_github' || err.body.nextAction === 'check_github_status')
      ) {
        setGithubErrorMsg(err.message);
        setGithubErrorNextAction(err.body.nextAction as 'reconnect_github' | 'check_github_status');
      } else if (err instanceof ApiError && Array.isArray(err.body.cohortTeamIds)) {
        // #2013 — the "more than one cohort team" 400
        // (routes/projects.ts:741-746, `{ error, cohortTeamIds }`). This
        // is the ONLY path that ever calls /api/academic/my-cohorts/teams
        // — deliberately reactive, not an on-open fetch, so every
        // 0-or-1-team creator's flow (everyone today) is byte-for-byte
        // unchanged. cohortTeamIds alone has no names for a picker; fetch
        // them now and filter to just the ambiguous set.
        try {
          const ids = new Set(err.body.cohortTeamIds as string[]);
          const { teams } = await apiFetch<{ teams: CohortTeamOption[] }>('/api/academic/my-cohorts/teams');
          const matched = teams.filter(t => ids.has(t.id));
          if (matched.length > 0) {
            setCohortTeamOptions(matched);
            setError('You belong to more than one team — choose which one this project is for below.');
          } else {
            // Defensive — names endpoint returned nothing matching.
            // Same degraded path as a names-fetch failure below.
            setError(err.message);
          }
        } catch {
          // Non-fatal: fall back to the API's own message. A degraded
          // path (raw error, no picker) beats an unusable modal.
          setError(err.message);
        }
      } else if (err instanceof ApiError && Array.isArray(err.body.details) && err.body.details.length > 0) {
        // #3037 AC2 — a Zod 400 (middleware/validate.ts). github.owner and
        // github.repo go next to their inputs; every other message goes to
        // the banner as "path: message", joined with '; ' (Zod's messages
        // don't name the field). All mapped → the banner stays empty.
        const { mapped, unmapped } = fieldErrorsFromDetails(err, 'github');
        const { owner, repo, ...otherGithub } = mapped;
        const bannerEntries = [
          ...Object.entries(otherGithub).map(([field, message]) => ({ path: `github.${field}`, message })),
          ...unmapped,
        ];
        const bannerText = bannerEntries
          .filter(e => e.message.trim())
          .map(e => (e.path ? `${e.path}: ${e.message}` : e.message))
          .join('; ');
        setGithubFieldErrors({ ...(owner ? { owner } : {}), ...(repo ? { repo } : {}) });
        // Nothing readable for the banner and nothing mapped → the API's own text.
        setError(bannerText.trim() || owner || repo ? bannerText : err.message);
        setFieldErrorSeq(n => n + 1);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to create project');
      }
    } finally {
      setLoading(false);
    }
  };

  const modalInputStyle: React.CSSProperties = {
    display: 'block',
    width: '100%',
    height: 40,
    background: 'var(--surface-alt)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)',
    padding: '0 12px',
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    color: 'var(--text)',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const modalSelectStyle: React.CSSProperties = {
    ...modalInputStyle,
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath d='M3 4.5L6 7.5L9 4.5' stroke='%236B7280' stroke-width='1.5' fill='none'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    paddingRight: 32,
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }} onClick={onClose} />
      <div className="relative w-full max-w-[480px] max-h-[85vh] flex flex-col" style={{ background: 'var(--surface)', borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-xl)' }}>
        {/* Header — fixed */}
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid var(--border)' }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>Create Project</h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            style={{ color: 'var(--text-secondary)', fontSize: 18, lineHeight: 1, cursor: 'pointer', background: 'none', border: 'none' }}
            onMouseEnter={e => (e.currentTarget.style.color = 'var(--text)')}
            onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-secondary)')}
          >
            ✕
          </button>
        </div>

        {/* Mode toggle */}
        <div className="px-6 pt-4 flex gap-1" style={{ background: 'var(--surface-alt)', borderBottom: '1px solid var(--border)' }}>
          <button
            type="button"
            style={{
              padding: '8px 16px',
              fontFamily: 'var(--font-body)',
              fontSize: 13,
              fontWeight: 500,
              borderRadius: '6px 6px 0 0',
              border: '1px solid var(--border)',
              borderBottom: '1px solid var(--surface)',
              background: 'var(--surface)',
              color: 'var(--text)',
              cursor: 'pointer',
              marginBottom: -1,
            }}
          >
            New Project
          </button>
          <button
            type="button"
            onClick={() => { onClose(); navigate('/onboard'); }}
            style={{
              padding: '8px 16px',
              fontFamily: 'var(--font-body)',
              fontSize: 13,
              fontWeight: 500,
              borderRadius: '6px 6px 0 0',
              border: 'none',
              background: 'transparent',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
            }}
          >
            Onboard Existing Project
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 min-h-0 flex flex-col">
          {/* Scrollable content */}
          <div ref={scrollContainerRef} className="flex-1 overflow-y-auto min-h-0 px-6 py-5">
          {/* #3037 Q5 — role="alert" so the error is announced wherever focus is. */}
          {error && (
            <div role="alert" data-testid="create-project-error" style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--error)', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 'var(--radius-sm)', padding: '8px 12px', marginBottom: 16 }}>
              {error}
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}>
              Project Name <span style={{ color: 'var(--error)' }}>*</span>
            </label>
            <input
              type="text"
              required
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Blueprint Platform"
              style={modalInputStyle}
              onFocus={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.12)'; }}
              onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}>
              Product Area{' '}
              <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>(optional)</span>
            </label>
            <input
              type="text"
              value={productArea}
              onChange={e => setProductArea(e.target.value)}
              placeholder="e.g. Core Platform, Mobile App"
              style={modalInputStyle}
              onFocus={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.12)'; }}
              onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
            />
          </div>

          {/* #2013 — only populated (and rendered) after a create attempt
              hits the "more than one cohort team" 400 — see handleSubmit's
              catch block. Never shown on first render. */}
          {cohortTeamOptions.length > 0 && (
            <div style={{ marginBottom: 16 }} data-testid="cohort-team-picker">
              <label style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}>
                Which team is this project for? <span style={{ color: 'var(--error)' }}>*</span>
              </label>
              <select
                required
                value={selectedCohortTeamId}
                onChange={e => setSelectedCohortTeamId(e.target.value)}
                style={modalSelectStyle}
                onFocus={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.12)'; }}
                onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <option value="" disabled>Select a team…</option>
                {cohortTeamOptions.map(t => (
                  <option key={t.id} value={t.id}>{t.cohortName} — {t.name}</option>
                ))}
              </select>
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}>
              Starting Phase
            </label>
            <select
              value={startingPhase}
              onChange={e => setStartingPhase(e.target.value)}
              style={modalSelectStyle}
              onFocus={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.12)'; }}
              onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              {PHASES.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>

          {/* MODE.B1 (#1174) — extracted picker. Modal owns the state
              (deliveryMode + selectedCheckpoints) so submit-time
              payload assembly and the create-button disable guard can
              read it directly. waivedPhases is intentionally omitted
              for the modal flow — it has no Prior Phases step.
              MODE.Cfix.A1 — shown to every tenant (the MODE.A2 Trial-hide
              is removed). Its default is Governed; the submit handler
              always sends the explicit choice. Mode can still be changed
              post-creation via PATCH /projects/:id. */}
          <div data-testid="delivery-mode-section-wrapper" style={{ marginBottom: 16 }}>
            <DeliveryModePicker
              value={deliveryMode}
              onChange={setDeliveryMode}
              checkpoints={selectedCheckpoints}
              onCheckpointsChange={setSelectedCheckpoints}
            />
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <label
                htmlFor="domain-pack-select"
                style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, color: 'var(--text)' }}
              >
                Project Domain
              </label>
              <HelpPopover
                id="domain-pack-selection"
                variant="info"
                heading="Domain pack consequences"
                body={
                  <>
                    <p style={{ margin: '0 0 8px' }}>
                      Your project domain changes how agents work, not just how the project is labelled.
                    </p>
                    <p style={{ margin: '0 0 8px' }}>
                      Agents pick up the domain at next session start and adjust their prompts, gate criteria, and review checks accordingly. <strong>Healthcare</strong> adds HIPAA, NABH, and WCAG 2.1 AA criteria. <strong>BFSI</strong> adds RBI and SEBI cyber-resilience criteria. <strong>Generic</strong> uses no industry-specific overlay.
                    </p>
                    <p style={{ margin: 0 }}>
                      Pick the domain that matches your project&apos;s regulatory context. If your project genuinely doesn&apos;t have one, Generic is correct — choosing a regulated domain just to be safe adds gate friction without compliance benefit.
                    </p>
                  </>
                }
              />
            </div>
            <select
              id="domain-pack-select"
              data-testid="domain-pack-select"
              value={domainPack}
              onChange={e => setDomainPack(e.target.value as DomainPackValue)}
              style={modalSelectStyle}
              onFocus={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.12)'; }}
              onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              {DOMAIN_PACKS.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-secondary)', marginTop: 6 }}>
              Adds regulatory context to every agent session for this project. You can change it later in the Domain Pack card on the project&apos;s Overview.
            </p>
          </div>

          {/* PROTO.A1 (#1619) — D7: plain conditional render, no
              feature-flag framework. Non-opted-in state (the default) is
              byte-identical to a build without this block — just the one
              toggle row. */}
          <div style={{ marginTop: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                data-testid="prototype-agent-toggle"
                checked={prototypeAgentEnabled}
                onChange={e => handlePrototypeAgentToggle(e.target.checked)}
              />
              <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>
                Generate a visual prototype alongside the PRD
              </span>
            </label>

            {/* ADR-003 (2026-07-31) — no fidelity selector: HIGH is the
                Prototype Agent's only fidelity, so enabling the toggle above
                is the only decision left. The tradeoff acknowledgement gate
                itself (view-then-acknowledge, persisted acknowledgedAt/
                tradeoffCopyVersion) is unchanged from PROTO.A1's D8. */}
            {prototypeAgentEnabled && (
              <div style={{ marginTop: 12, paddingLeft: 26 }}>
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-secondary)', margin: '0 0 8px' }}>
                  {PROTOTYPE_AGENT_GOVERNING_PRINCIPLE}
                </p>

                {!tradeoffCopyViewed ? (
                  <button
                    type="button"
                    data-testid="prototype-tradeoff-view-toggle"
                    onClick={() => setTradeoffCopyViewed(true)}
                    style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--primary)', background: 'none', border: 'none', textDecoration: 'underline', cursor: 'pointer', padding: 0, marginBottom: 10 }}
                  >
                    Read the tradeoffs &rarr;
                  </button>
                ) : (
                  <div
                    data-testid="prototype-tradeoff-copy"
                    style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', background: 'var(--surface-alt)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: 12, marginBottom: 10 }}
                  >
                    <p style={{ margin: '0 0 6px' }}><strong>What you get:</strong> {PROTOTYPE_TRADEOFF_ENTRY.whatYouGet}</p>
                    <p style={{ margin: '0 0 6px' }}><strong>Best for:</strong> {PROTOTYPE_TRADEOFF_ENTRY.bestFor}</p>
                    <p style={{ margin: 0 }}><strong>Tradeoff:</strong> {PROTOTYPE_TRADEOFF_ENTRY.tradeoff}</p>
                  </div>
                )}

                {/* D8 — mirrors PartnerOnboardingWizardPage.tsx:828-835
                    exactly: checkbox disabled until viewed === true,
                    never enabled by default or by mere presence of the
                    copy in the DOM. */}
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                    cursor: tradeoffCopyViewed ? 'pointer' : 'not-allowed',
                    opacity: tradeoffCopyViewed ? 1 : 0.6,
                  }}
                >
                  <input
                    type="checkbox"
                    data-testid="prototype-tradeoff-ack"
                    checked={tradeoffCopyAcknowledged}
                    disabled={!tradeoffCopyViewed}
                    onChange={e => setTradeoffCopyAcknowledged(e.target.checked)}
                    style={{ marginTop: 2 }}
                  />
                  <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>
                    I understand the tradeoffs of generating a prototype.
                  </span>
                </label>
              </div>
            )}
          </div>

          {/* #1115B S8 — GitHub repository setup section. Mounted between
              Domain Pack (above) and the footer. Modal mode: no own submit,
              emits payload via onChange. The collected payload rides on the
              POST /projects body as `github: { mode, ... }`. Omission is
              valid (BootstrapAgent's existing default path runs as before).

              #1153 D5 + V4 — controlled error props surface bind-step failures
              inline within the section. `onModeChange` clears the error when
              the user toggles to a different radio option (recovery intent);
              `onRetry` clears it when the user clicks the [Retry] button
              (check_github_status case). */}
          <div style={{ marginTop: 16 }}>
            <GitHubRepoSetupSection
              onChange={handleGithubPayloadChange}
              errorMsg={githubErrorMsg}
              errorNextAction={githubErrorNextAction}
              onRetry={clearGithubError}
              onModeChange={clearGithubError}
              fieldErrors={githubFieldErrors}
            />
          </div>
          </div>

          {/* Footer — always visible */}
          <div className="flex-shrink-0 flex justify-end gap-3 px-6 py-4" style={{ borderTop: '1px solid var(--border)' }}>
            <button
              type="button"
              onClick={onClose}
              style={{ padding: '8px 16px', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', transition: 'background 150ms' }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface-alt)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'none')}
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="create-project-submit"
              disabled={loading || !name.trim() || minCheckpointGuard || prototypeAgentGuard || cohortTeamPickerGuard || githubLinkGuard}
              style={{
                padding: '8px 16px',
                fontFamily: 'var(--font-display)',
                fontSize: 13,
                fontWeight: 600,
                color: 'white',
                background: 'var(--primary)',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                cursor: (loading || !name.trim() || minCheckpointGuard || prototypeAgentGuard || cohortTeamPickerGuard || githubLinkGuard) ? 'not-allowed' : 'pointer',
                opacity: (loading || !name.trim() || minCheckpointGuard || prototypeAgentGuard || cohortTeamPickerGuard || githubLinkGuard) ? 0.5 : 1,
                transition: 'box-shadow 150ms',
              }}
              onMouseEnter={e => { if (!e.currentTarget.disabled) e.currentTarget.style.boxShadow = 'var(--shadow-primary)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
            >
              {loading ? 'Creating\u2026' : 'Create Project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
